/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @next/next/no-img-element */
'use client';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import Modal from '@fe-common-ui/Modal';
import type { ModalHandle } from '@fe-common-ui/Modal/Modal';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import NewsFeatureReport, { GTMComponentSource } from '@fe-news/components/Aside/NewsFeatureReport/NewsFeatureReport';
import QueryProvider from '@fe-news/context/query-provider';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { styled } from '@linaria/react';
import { usePathname } from 'next/navigation';
import { memo, useEffect, useRef, useState } from 'react';
import AnueBottomService from './AnueBottomService/AnueBottomService';
import HotNews from './HotNews/HotNews';
import ToggleTopAd from './ToggleTopAd';

/**
 * 桌機閒置彈跳視窗
 * 當使用者閒置 3 秒後，彈出視窗
 */
const POP_AD_ORDER = 99_990;
const DELAY_TIME = 3 * 60 * 1000;
const CARD_HEIGHT = 271;
const GTM_EVENT_IMP_PLACEMENT = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.IMP_PLACEMENT,
  [GTMEventKey.SECTION]: 'News_Timed_Popup'
};

const ModalWrapper = styled.div`
  width: 920px;
  height: 750px;
  padding: 20px;
  border-radius: 10px;
  background-color: #f6f8fc;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  :global(.desktop-cover-overlay) {
    z-index: 100;
  }
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  padding: 6px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(246, 248, 252, 0.9);
  z-index: 2;
`;

const TopWrapper = styled.div<{ isShowLiTV?: boolean }>`
  display: flex;
  align-items: center;
  overflow: hidden;

  #LiTV {
    width: 100%;
    height: ${({ isShowLiTV }) => (isShowLiTV ? 285 : 215)}px;
    margin: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 24px;
  margin-top: 10px;
`;

const NewsFeatureWrapper = styled.div`
  width: 334px;

  && h3 {
    padding-left: 22px;
  }
`;

const onClickModal = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

const CoverDeskTopModal = () => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`);
  const ModalReference = useRef<ModalHandle | null>(null);
  const timerReference = useRef<NodeJS.Timeout | null>(null);
  const [isDisplayLiTV, setIsDisplayLiTV] = useState(false);
  const pathName = usePathname();

  const onCloseModal = (): void => {
    if (ModalReference.current) {
      ModalReference.current.hide();
      ModalReference.current.close();
    }
  };

  useEffect(() => {
    timerReference.current = setTimeout(() => {
      if (isDesktop && ModalReference.current) {
        ModalReference.current.open();
        ModalReference.current.show();

        sendGTM(GTM_EVENT_IMP_PLACEMENT);
      }
    }, DELAY_TIME);

    return () => {
      if (timerReference.current) clearTimeout(timerReference.current);
    };
  }, [isDesktop, pathName]);

  // TODO: 待重構 Modal 的處理，可以分成二種 Modal 來處理廣告 Modal 和一般 Modal，因為 open 和 show 的行為不同
  return (
    <Modal
      ref={ModalReference}
      disableButton={
        <CloseButton onClick={onCloseModal}>
          <img src='/assets/icons/close/modal-icon-close.svg' alt='close icon' />
        </CloseButton>
      }
      disableClose={false}
      defaultHide
      modalClassName='desktop-cover-overlay'
      zIndex={POP_AD_ORDER}
    >
      <QueryProvider>
        <ModalWrapper onClick={onClickModal}>
          <TopWrapper className='modal-top-wrapper' isShowLiTV={isDisplayLiTV}>
            <ToggleTopAd isShowLiTV={isDisplayLiTV} setIsDisplayLiTV={setIsDisplayLiTV} />
          </TopWrapper>
          <ContentWrapper>
            <NewsFeatureWrapper>
              <NewsFeatureReport
                isShowIndicators={false}
                cardHeight={CARD_HEIGHT}
                carouselHeight={CARD_HEIGHT}
                source={GTMComponentSource.POPUP}
              />
            </NewsFeatureWrapper>
            <HotNews />
          </ContentWrapper>
          <AnueBottomService isOnlyChild={isDisplayLiTV} />
        </ModalWrapper>
      </QueryProvider>
    </Modal>
  );
};

export default memo(CoverDeskTopModal);
